import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withPreloading } from '@angular/router';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { appRoutes } from './app.routes';
import { PermissionAwarePreloadStrategy } from './core/permission-aware-preload.strategy';
import { crmAuthInterceptor } from './auth/crm-auth.interceptor';
import { crmAuthErrorInterceptor } from './auth/crm-auth-error.interceptor';
import { requestDedupInterceptor } from './request-dedup.interceptor';
import {
  API_TOAST_OWNS_UNAUTHORIZED,
  apiToastInterceptor,
} from '../../../../libs/core/service/api-toast.interceptor';
import { API_FIELD_ERRORS_ENABLED } from '../../../../libs/core/service/api-field-errors.service';
import { AppErrorHandler } from './core/app-error-handler';
import { crmBrandingActivityInterceptor } from './core/crm-branding-activity.interceptor';
import { preCheckPageSaveInterceptor } from './pages/cases/case-view/case-review/pre-check/pre-check-page-save';
// By file, not through the `libs/core` barrel — see the note in app.ts.
import { CRM_REMEMBER_ME_KEY, migrateLegacyRememberMe } from '../../../../libs/core/auth/remember-me';

export const appConfig: ApplicationConfig = {
  providers: [
    // provideZonelessChangeDetection() was removed intentionally.
    //
    // This app uses the classic zone.js change detection model:
    //   - RxJS .subscribe() callbacks that mutate normal component properties
    //   - PrimeNG components that rely on zone-based CD
    //   - NgZone.run() calls in existing components
    //   - No signals-only state management
    //
    // Zoneless CD requires ALL state to be Angular signals or use the async pipe.
    // Without zone.js, templates only refresh on user interactions (click/scroll),
    // which is exactly the "stale UI after API calls" bug we experienced.
    //
    // zone.js is imported in main.ts — Angular auto-detects it and uses it.
    { provide: ErrorHandler, useClass: AppErrorHandler },
    // Older builds stored the login password as readable plaintext under the
    // "remember me" key. Re-encrypt at bootstrap rather than leaving it to the
    // login component: a user who ticked the box and is still signed in never
    // mounts /login, so the plaintext would otherwise sit in their localStorage
    // indefinitely. Not awaited — prefilling the login form is not something
    // app startup should block on.
    provideAppInitializer(() => {
      void migrateLegacyRememberMe(CRM_REMEMBER_ME_KEY);
    }),
    // crmAuthErrorInterceptor is the single owner of 401: it refreshes the token
    // when possible and otherwise runs a full logout (cookies, branding,
    // permissions, logout API) with a session-expired toast. apiToastInterceptor
    // must not also react to 401 — it only sees mutations and its shallow logout
    // would race the refresh.
    { provide: API_TOAST_OWNS_UNAUTHORIZED, useValue: false },
    // A rejected save shows the backend's own message (a page's generic toast can't
    // replace it) and highlights + scrolls to the field it names, in pages and popups.
    { provide: API_FIELD_ERRORS_ENABLED, useValue: true },
    provideClientHydration(withEventReplay()),
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(
      withFetch(),
      withInterceptors([crmAuthInterceptor, crmAuthErrorInterceptor, requestDedupInterceptor, apiToastInterceptor, crmBrandingActivityInterceptor, preCheckPageSaveInterceptor]),
    ),
    // Lazy chunks are preloaded in the background after the app is interactive,
    // so navigating between feature modules is instant without slowing first load.
    //
    // Was PreloadAllModules, which pulled all ~140 chunks — the whole app —
    // including feature modules the signed-in user has no permission to open.
    // PermissionAwarePreloadStrategy keeps the instant-navigation benefit and
    // drops only the modules the permission tree says are unreachable; anything
    // it cannot classify still preloads exactly as before.
    provideRouter(appRoutes, withPreloading(PermissionAwarePreloadStrategy)),
  ],
};
